// Who is holding the browser, as far as this client is concerned: a bearer token.
//
// `docs/ARCHITECTURE.md` §1: "token-based auth rather than cookie sessions". Subcomponent 2
// verifies a token minted by an identity vendor; there is no login route on this API and
// this package deliberately does not invent one. What it owns is where the token lives
// between page loads and the fact that it goes in a header.
//
// `sessionStorage`, not `localStorage`: the token dies with the tab. A plan is a family's
// conversation about a parent's death, often read on a shared laptop, and "still signed in
// tomorrow" is not worth what it costs on that laptop. It is also not a cookie, so nothing
// here is attached to a request the person did not make.
//
// This module is a store rather than a React context, and `useSyncExternalStore` reads it.
// That is not stylistic: the API client and the signal stream both need the current token
// from outside the render tree, and a context would mean either a second copy or a
// callback threaded through everything that fetches.

import { clearDrafts } from './draft-stash.js';

const STORAGE_KEY = 'mor.session.token';

type Listener = () => void;

const listeners = new Set<Listener>();
let token: string | null = readStored();

function readStored(): string | null {
  try {
    return globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    // Storage can be unavailable — private mode, an embedded webview, a blocked origin.
    // A session that lasts one page load is a worse experience and a working one.
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value === null) {
      globalThis.sessionStorage?.removeItem(STORAGE_KEY);
    } else {
      globalThis.sessionStorage?.setItem(STORAGE_KEY, value);
    }
  } catch {
    // As above.
  }
}

export const session = {
  token(): string | null {
    return token;
  },
  signIn(value: string): void {
    token = value.trim() === '' ? null : value.trim();
    writeStored(token);
    for (const listener of listeners) {
      listener();
    }
  },
  signOut(): void {
    clearDrafts();
    token = null;
    writeStored(null);
    for (const listener of listeners) {
      listener();
    }
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export type Session = typeof session;
